import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  Loader2,
  MinusCircle,
  ShieldAlert,
  UserCheck,
  X,
  XCircle,
  XOctagon,
} from 'lucide-react';
import { usePatientPipelineStatus } from '../patient/usePatientPipelineStatus';
import {
  areStepsFinal,
  isPatientPipelineApiAvailable,
  type PipelineContinueGate,
  type PipelineImportStep,
} from '../../api/patientPipelineApi';
import {
  adoptRecoveredPatientConnection,
  clearPatientConnectionState,
  closeSmartSession,
  extractEchoedHistoryWindow,
  fetchSmartImportPreview,
  fetchSmartPatient,
  getStoredSmartHistoryWindow,
  getStoredSmartWorkspacePatient,
  normalizeSmartPatient,
  resolveSmartSessionFromLocationDetailed,
  SMART_SESSION_NOT_FOUND_MESSAGE,
  storeSmartHistoryWindow,
  storeSmartWorkspacePatient,
  fetchSmartChartStatus,
  SMART_HISTORY_WINDOW_LABELS,
  waitForSessionChartReady,
  CHART_READY_SLOW_AFTER_MS,
  type SmartChartPhase,
  type SmartHistoryWindow,
} from '../../api/smartApi';
import { TwinReadinessNotice } from './TwinReadinessNotice';
import type { SmartPatientDemographics } from '../../types/smart';
import { Slider } from '../ui/slider';
import { cn } from '../ui/utils';
import { hasMarchAgentBearer, requiresMarchAgentBearer } from '../../api/marchApiEnv';
import { reconcileClinicianScope } from '../../api/smartStorageScope';
import {
  type ActiveContext,
  type ClaimInput,
  getPatientContextCoordinator,
} from '../../api/patientContextCoordinator';
import { shouldBypassSmartLaunch } from '../../auth/authMode';
import {
  applyPendingSmartLaunchSession,
  capturePendingSmartLaunchFromLocation,
  consumeSmartLaunchHandoff,
  getPendingSmartLaunch,
  smartLaunchElapsedMs,
  validatePendingSmartLaunch,
} from '../../auth/pendingSmartLaunch';

type GateState =
  | { status: 'checking'; phase?: 'patient' | 'import' }
  | { status: 'ready' }
  // The clinician has committed to this patient and window, and the chart is
  // being fetched and loaded. Holding them here — rather than opening the chat
  // immediately — is the point: the chat used to answer "the chart import is
  // still in progress" to every question asked during this period.
  | {
      status: 'preparing';
      sessionId: string;
      patient: SmartPatientDemographics;
      phase: SmartChartPhase;
      detail: string;
      /** Set once the wait exceeds the comfortable threshold, which reveals a
       *  way into the chat so nobody is ever stuck watching a spinner. */
      slow: boolean;
      /** The chart itself can answer questions now. This is NOT the same as
       *  "everything is finished": the SDOH/trajectory and patient-context
       *  steps keep running for several more seconds afterwards. It gates the
       *  Continue button, it no longer opens the chat by itself (#38). */
      chartReady: boolean;
    }
  | { status: 'blocked'; message?: string }
  | { status: 'error'; message: string }
  // First attach of a session (a genuine fresh EHR launch): the clinician
  // must pick how much chart history to load before the import runs — this
  // is the ONLY point at which the window choice is interactive.
  | { status: 'select-window'; sessionId: string; patient: SmartPatientDemographics }
  // A recovered (cross-tab) session was found: sessionStorage was gone, so
  // the app was closed and re-opened, and the session came from the recovery
  // cache — whichever patient was launched most recently in ANY tab. The
  // clinician must confirm the identity before this tab adopts it (the
  // wrong-patient safety boundary), and the window picker is interactive:
  // re-opening the app is, by product rule, the only route to changing the
  // history window. A same-tab return (e.g. from Admin) never reaches this
  // state — it re-enters the chat directly (USER ORDER 2026-07-31).
  | { status: 'confirm-recovered'; sessionId: string; patient: SmartPatientDemographics }
  // This tab launched a DIFFERENT patient while another tab of the same
  // clinician is already live on one — the single-active-patient conflict.
  | { status: 'conflict'; sessionId: string; patient: SmartPatientDemographics; existing: ActiveContext }
  // Another tab took over the active patient context; this tab must stop
  // showing its (now stale) patient to preserve the single-active-patient rule.
  | { status: 'superseded'; active: ActiveContext };

/** Stable patient identity + label for the cross-tab coordinator. */
function claimInputFor(sessionId: string, patient: SmartPatientDemographics): ClaimInput {
  return {
    sessionId,
    patientId: patient.fhirId || patient.mrn || patient.accountNumber || sessionId,
    patientLabel: patient.displayName || 'Current patient',
  };
}

const _GATE_STEP_TIMEOUT_MS = 30_000;

/** A hung backend must never strand the clinician on an infinite spinner —
 * every gate step gets a deadline that degrades into the retryable error state. */
async function _withTimeout<T>(promise: Promise<T>, step: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Timed out while ${step}. The service may be starting up — retry in a moment.`)),
      _GATE_STEP_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([promise, deadline]);
  } finally {
    clearTimeout(timer);
  }
}

function GateShell({ title, message, children }: { title: string; message?: string; children?: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-xl rounded-3xl border border-border bg-surface-raised p-8 text-center shadow-sm">
        <h1 className="text-xl font-medium">{title}</h1>
        {message ? <p className="mt-3 text-sm leading-6 text-text-tertiary">{message}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** Name + MRN identity card shown at both the first-attach window-selection
 * step and the (disabled) resume/recovery confirmation — one shared shape
 * per the design so the two screens never drift apart. */
function PatientIdentityBox({ displayName, identifier }: { displayName: string; identifier: string }) {
  return (
    <div className="mx-auto mt-5 flex max-w-sm items-center gap-3 rounded-2xl border border-border bg-background/70 p-4 text-left">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-primary" aria-hidden>
        <UserCheck className="size-5" />
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground">{displayName}</div>
        {identifier ? (
          <div className="truncate font-mono text-xs text-text-tertiary">MRN {identifier}</div>
        ) : null}
      </div>
    </div>
  );
}

/** The five chart-history windows the clinician can load, in slider order
 * (position 0 = fastest/shortest ... last = full history). Labels come from
 * SMART_HISTORY_WINDOW_LABELS — the single source of truth shared with the
 * chat header's window badge, so the two can never show different wording
 * for the same code. */
const HISTORY_WINDOW_OPTIONS: { value: SmartHistoryWindow; label: string }[] = (
  ['1y', '2y', '5y', '10y', 'all'] as const
).map((value) => ({ value, label: SMART_HISTORY_WINDOW_LABELS[value] }));

/** Always used on a fresh launch — never read from storage (a remembered
 * choice must never silently pre-select a default). */
const DEFAULT_HISTORY_WINDOW: SmartHistoryWindow = '2y';

function historyWindowIndex(value: SmartHistoryWindow): number {
  const idx = HISTORY_WINDOW_OPTIONS.findIndex((opt) => opt.value === value);
  return idx >= 0 ? idx : HISTORY_WINDOW_OPTIONS.findIndex((opt) => opt.value === DEFAULT_HISTORY_WINDOW);
}

/** The solid, filled "Continue to {name}" action on both window-picker
 * screens (first-attach and the interactive re-opened-app path) — teal fill
 * with near-black text per the approved mockup. `--p-cool-16` is a raw
 * (theme-invariant) primitive, not a light/dark-swapped token, so the text
 * stays dark-on-teal in both themes. Scoped to this one button on purpose —
 * the Switch/Keep/Go-to/Try-again buttons elsewhere on this gate keep their
 * existing look. */
const CONTINUE_BUTTON_CLASSES =
  'inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-lg bg-primary px-5 text-sm font-semibold text-(--p-cool-16) transition-colors hover:bg-primary/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:w-auto';

/**
 * Five separately-boxed, evenly-spaced buttons for picking the chart-history
 * window. The approved mockup shows discrete rounded rectangles with a gap
 * between them, not Radix ToggleGroup's fused/shared-edge look (which is
 * still the right primitive elsewhere) — so this is a small local
 * `radiogroup`/`radio` implementation, kept private to this file. Arrow
 * keys move both the selection and focus (roving tabindex), matching the
 * keyboard pattern a native radiogroup gives for free.
 */
function WindowOptionRow({
  value,
  onChange,
  disabled = false,
}: {
  value: SmartHistoryWindow;
  onChange?: (next: SmartHistoryWindow) => void;
  disabled?: boolean;
}) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const currentIndex = historyWindowIndex(value);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = Math.min(currentIndex + 1, HISTORY_WINDOW_OPTIONS.length - 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = Math.max(currentIndex - 1, 0);
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = HISTORY_WINDOW_OPTIONS.length - 1;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const opt = HISTORY_WINDOW_OPTIONS[nextIndex];
    onChange?.(opt.value);
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons[nextIndex]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Chart history window"
      className="grid grid-cols-5 gap-2"
      onKeyDown={handleKeyDown}
    >
      {HISTORY_WINDOW_OPTIONS.map((opt) => {
        const checked = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={opt.label}
            disabled={disabled}
            tabIndex={disabled || !checked ? -1 : 0}
            onClick={() => {
              if (!disabled) onChange?.(opt.value);
            }}
            className={cn(
              'min-h-13 whitespace-normal rounded-lg border px-2 py-2.5 text-center text-sm font-medium leading-tight transition-colors disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
              checked
                ? 'border-brand-border bg-brand-subtle text-brand-text'
                : 'border-border bg-surface-hover text-foreground hover:bg-surface-sunken',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The option row + mirrored slider for choosing how much chart history to
 * load. One piece of state (`value`) drives both controls, so they can
 * never disagree. `disabled` renders both controls fully non-interactive
 * (no click, no drag, no keyboard focus) — used for the read-only "session
 * already has this window" display on every return to chat after the first
 * attach on the same tab.
 */
function HistoryWindowSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: SmartHistoryWindow;
  onChange?: (next: SmartHistoryWindow) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mx-auto mt-5 max-w-sm text-left">
      <WindowOptionRow value={value} onChange={onChange} disabled={disabled} />
      <div className="mt-4">
        <div className="text-xs font-medium text-text-tertiary">Response speed vs. chart depth</div>
        <Slider
          className="mt-2.5"
          variant="gradient"
          min={0}
          max={HISTORY_WINDOW_OPTIONS.length - 1}
          step={1}
          disabled={disabled}
          value={[historyWindowIndex(value)]}
          onValueChange={(next) => {
            if (disabled) return;
            const opt = HISTORY_WINDOW_OPTIONS[next[0]];
            if (opt) onChange?.(opt.value);
          }}
        />
        <div className="mt-1.5 flex items-center justify-between text-[11px] text-text-tertiary">
          <span>Fastest</span>
          <span>Full history</span>
        </div>
      </div>
    </div>
  );
}

/**
 * The first-attach gate step (a genuine EHR launch). Always starts at the
 * "2 years" default — never remembered from a previous session. The
 * re-opened-app path (ConfirmRecoveredStep, `source === 'recovered'`) is
 * interactive the same way; only the same-tab resume path (`source ===
 * 'tab'`) keeps the window locked.
 */
function SelectWindowStep({
  displayName,
  identifier,
  onContinue,
}: {
  displayName: string;
  identifier: string;
  onContinue: (window: SmartHistoryWindow) => void;
}) {
  const [selected, setSelected] = useState<SmartHistoryWindow>(DEFAULT_HISTORY_WINDOW);
  return (
    <GateShell title="Active patient">
      <PatientIdentityBox displayName={displayName} identifier={identifier} />
      <p className="mx-auto mt-4 max-w-sm text-left text-sm leading-6 text-text-tertiary">
        Select how much chart history to load — shorter windows give faster responses.
      </p>
      <HistoryWindowSelector value={selected} onChange={setSelected} />
      <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => onContinue(selected)}
          className={CONTINUE_BUTTON_CLASSES}
        >
          Continue to {displayName}
        </button>
      </div>
    </GateShell>
  );
}

/**
 * The re-opened-app confirmation screen (`source === 'recovered'` only).
 *
 * sessionStorage was empty and the session was found only in the cross-tab
 * localStorage recovery cache, meaning the tab was closed and the app was
 * re-opened. That is functionally the same event as a fresh app-open, so the
 * window picker is INTERACTIVE — defaulted to "2 years", never read from
 * storage — and identity confirmation is required before the session is
 * adopted (the recovered session is whichever patient was launched most
 * recently in ANY tab: a wrong-patient hazard without the confirm).
 *
 * A same-tab return (e.g. from Admin) no longer reaches this screen at all —
 * it re-enters the chat directly (USER ORDER 2026-07-31).
 */
function ConfirmRecoveredStep({
  displayName,
  identifier,
  onContinue,
}: {
  displayName: string;
  identifier: string;
  onContinue: (window: SmartHistoryWindow) => void;
}) {
  const [selected, setSelected] = useState<SmartHistoryWindow>(DEFAULT_HISTORY_WINDOW);
  return (
    <GateShell title="Active patient">
      <PatientIdentityBox displayName={displayName} identifier={identifier} />
      <p className="mx-auto mt-4 max-w-sm text-left text-sm leading-6 text-text-tertiary">
        Select how much chart history to load — shorter windows give faster responses.
      </p>
      <HistoryWindowSelector value={selected} onChange={setSelected} />
      <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => onContinue(selected)}
          className={CONTINUE_BUTTON_CLASSES}
        >
          Continue to {displayName}
        </button>
      </div>
    </GateShell>
  );
}

/**
 * Ticket #36: the per-step checklist for the SMART waiting window (ticket
 * #35's journal, one row per pipeline stage — "FHIR chart retrieval",
 * "Digital Twin profile load", "Chart digest assembly"…). The labels
 * themselves are owned by the backend (`_STEP_LABELS` in
 * import_step_journal.py) and arrive on each step; nothing here hardcodes
 * them.
 * Spinner while running, green check + elapsed time when done, red icon +
 * short error when failed — the confirmed spec. Sourced from
 * `/pipeline-status`'s `steps`/`continue_gate` fields (ticket #35/#36
 * backend), polled independently of the chart-ready wait above so a chart
 * whose journal never resolves still shows the existing spinner/detail line
 * unaffected — this checklist is a strictly additive display, it does not
 * change when the clinician is allowed to continue.
 */
/**
 * Same anatomy for both gating and non-gating (ticket #39) rows: green check
 * when done, spinner while running, and an amber/warn treatment (never the
 * red fatal styling reserved for the top-level `GateWarning`) with the
 * `error_short` text when a step failed -- a background LLM product failing
 * is not the same severity as a chart-import failure.
 *
 * Task #11: with the waiting window now a pure reader (see chart_readiness.py
 * materialize_patient_summary), a warm-cache `skipped` result is the COMMON
 * case, not the rare one -- so it can no longer share `done`'s vivid green
 * check without the checklist reading as "everything just ran" when most of
 * it was reused.
 *
 * USER ORDER 2026-08-06: muting the SAME glyph was not enough. Reported live
 * on the Coy Samual Gutmann launch, where `date_window_filter` and
 * `trajectory_summary` were both skipped (cached chart, deduped prepare job)
 * and still read as ticked-off work at a glance -- the two rows that did the
 * LEAST looked like the six that ran. Skipped now uses a DIFFERENT glyph, a
 * dash-in-circle, so the distinction survives a glance and does not depend on
 * noticing a color difference:
 *   done    -> green check   (work ran, and succeeded)
 *   skipped -> muted dash    (work was not needed; nothing ran)
 *   error   -> amber X       (work ran, and failed)
 *   running -> spinner
 * A check mark now means, without exception, that this step actually
 * executed during THIS launch.
 */
function ImportStepRow({ step }: { step: PipelineImportStep }) {
  const elapsedLabel = step.elapsed_ms != null ? `${(step.elapsed_ms / 1000).toFixed(1)}s` : null;
  // A skip note or error reason can run much longer than an elapsed-time
  // value ("Chart unchanged, trajectory already up to date" vs "4.2s") --
  // rendering it in the same right-hand slot on the label's own row forced
  // the label itself to truncate ("Writing c...") to make room. Reported
  // live 2026-08-04 (patient Eleonore Buckridge). Fixed by giving the label
  // its own full-width row and rendering the longer secondary text on its
  // own line below, small and muted, indented under the label.
  const secondaryText =
    step.status === 'error'
      ? step.error_short || 'Failed'
      : step.status === 'skipped'
        ? step.note || 'Already up to date'
        : null;
  return (
    // `data-step-status` mirrors the journal's own status verbatim. The status
    // icons are aria-hidden decoration, so without this the rendered row
    // carries no machine-readable trace of whether the step ran, was skipped,
    // or failed -- which is both an a11y gap and what made the skipped-vs-done
    // distinction untestable before USER ORDER 2026-08-06.
    <div className="py-1.5 text-sm" data-step-status={step.status}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2 text-foreground">
          {step.status === 'done' ? (
            <CheckCircle2 className="size-4 shrink-0 text-status-positive-text" aria-hidden />
          ) : step.status === 'skipped' ? (
            <MinusCircle className="size-4 shrink-0 text-text-tertiary" aria-hidden />
          ) : step.status === 'error' ? (
            <XCircle className="size-4 shrink-0 text-status-attention-text" aria-hidden />
          ) : (
            <Loader2 className="size-4 shrink-0 animate-spin text-primary" aria-hidden />
          )}
          <span className="truncate">{step.label}</span>
        </span>
        {secondaryText == null ? (
          <span className="shrink-0 text-xs text-text-tertiary">{elapsedLabel}</span>
        ) : null}
      </div>
      {secondaryText != null ? (
        <p className="mt-0.5 pl-6 text-xs text-text-tertiary">{secondaryText}</p>
      ) : null}
      {step.warning ? (
        // Attention, not fatal: the step genuinely succeeded (the check ran
        // and answered "not there yet"), so this must never borrow the red
        // `GateWarning` treatment reserved for a chart that is wrong. Same
        // amber/warn token the row already uses for a non-gating step's own
        // failure, so severity stays visually consistent across the
        // checklist -- but on its own line, not squeezed next to the note.
        <p className="mt-0.5 pl-6 text-xs text-status-attention-text">{step.warning}</p>
      ) : null}
    </div>
  );
}

/**
 * Ticket #39: the gating steps (fhir_import/reasoning_context/
 * patient_context_package) render first, in journal order, exactly as
 * before. Any non-gating background step (`gating: false` -- e.g.
 * trajectory_summary, clinical_synthesis) renders as a secondary row below a
 * thin divider, in journal order, using the same `ImportStepRow` anatomy.
 * These rows are purely informational: `areStepsFinal`/the Continue gate
 * ignore them entirely, so a background step still running or errored can
 * never hold the clinician on this screen.
 */
function ImportStepChecklist({ steps }: { steps: PipelineImportStep[] }) {
  if (!steps.length) return null;
  const gatingSteps = steps.filter((step) => step.gating !== false);
  const backgroundSteps = steps.filter((step) => step.gating === false);
  return (
    <div className="mx-auto mt-5 max-w-sm rounded-2xl border border-border bg-background/70 p-4 text-left">
      {gatingSteps.map((step) => (
        <ImportStepRow key={step.step_id} step={step} />
      ))}
      {backgroundSteps.length ? (
        <div className="mt-1.5 border-t border-border pt-1.5">
          {backgroundSteps.map((step) => (
            <ImportStepRow key={step.step_id} step={step} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The gate's problem notice. Both severities are red on purpose — either one
 * means the clinician is about to work with a chart that is not what it should
 * be, and that has to be impossible to skim past.
 *
 * Red alone never carries the meaning (design-system rule: severity is never
 * colour-only). The icon separates the two: an octagon for the fatal stop that
 * offers no way forward, a shield for the one the clinician may pass with their
 * eyes open. `role="alert"` announces either to a screen reader on arrival.
 *
 * Deliberately NOT the shared `AlertBanner`: its title renders in
 * `text-foreground` at `font-medium`, and reshaping it to be red and bold here
 * would restyle every other caller of it across the app.
 */
function GateWarning({ tone, children }: { tone: 'fatal' | 'soft'; children: React.ReactNode }) {
  const Icon = tone === 'fatal' ? XOctagon : ShieldAlert;
  return (
    <div
      role="alert"
      className="mx-auto mt-4 flex max-w-sm items-start gap-2 rounded-lg border border-(--status-negative)/30 bg-status-negative-bg p-3 text-left"
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-status-negative-text" aria-hidden />
      <p className="text-xs font-bold leading-5 text-status-negative-text">{children}</p>
    </div>
  );
}

/** What the clinician is told during each stage of loading the chart. */
const CHART_PHASE_MESSAGE: Record<SmartChartPhase, string> = {
  importing: 'Getting the record from the clinical system…',
  building: 'Preparing the chart so it can answer questions…',
  ready: 'Chart ready.',
  // The check itself failed. Say nothing alarming — the chart is very likely
  // still loading normally behind a transient error, and the wait retries.
  unknown: 'Getting the record from the clinical system…',
};

/** How long after the chart itself is ready to keep waiting for the step
 *  journal before offering Continue anyway. The three steps take ~10s end to
 *  end on dev, so on the healthy path this never fires — it exists only so a
 *  missing journal, or a step stuck in `running`, can never strand the
 *  clinician on a screen with no way forward. */
const STEP_GATE_FALLBACK_MS = 30000;

/**
 * #56 (USER ORDER 2026-08-06): the overall cap on the waiting window, measured
 * from SMART LAUNCH START — not from when this screen mounts, and not from the
 * Continue click. Before this there was no overall cap at all: a stuck launch
 * waited forever.
 *
 * At the cap the clinician is warned and Continue is OFFERED. Nothing is
 * aborted server-side; the import and the background prepare job keep running,
 * and the chart keeps filling in behind the chat.
 *
 * The margin here is genuinely thin and that is a known, accepted trade: a
 * healthy cold launch of a 4,258-resource chart measured ~75s on 2026-08-06,
 * so a correct launch has only ~15s of headroom, and SDOH normalization scales
 * with how many of the patient's codes have never been seen before. A patient
 * with many novel codes can plausibly cross 90s while doing nothing wrong.
 * That is exactly why the copy below says the chart is still building and
 * never suggests a failure — crossing this line is slow, not broken.
 */
const SMART_LAUNCH_TIMEOUT_MS = resolveLaunchTimeoutMs();

function resolveLaunchTimeoutMs(): number {
  // Overridable so tests do not have to burn 90 seconds of wall clock, and so
  // the value can be tuned per environment without a code change. Ignored
  // unless it parses to a positive number.
  const raw = Number.parseInt(String(import.meta.env.VITE_SMART_LAUNCH_TIMEOUT_MS ?? ''), 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 90_000;
}

/**
 * True once this launch has been waiting longer than `timeoutMs`.
 *
 * Reads the start stamp written at launch handoff, so sign-in and the
 * history-window picker are inside the measured span. When no stamp exists
 * (a same-tab resume, or sessionStorage unavailable) the elapsed time is
 * unknowable, and the cap is measured from mount instead — deliberately
 * chosen because the failure this ticket fixes is waiting FOREVER, and a cap
 * measured from slightly too late still ends that, whereas no cap at all does
 * not. The distinction is recorded here so nobody later reads the mount-based
 * timer as the intended behaviour: it is the degraded path.
 */
function useSmartLaunchTimedOut(timeoutMs: number): boolean {
  const [timedOut, setTimedOut] = useState(() => (smartLaunchElapsedMs() ?? 0) >= timeoutMs);

  useEffect(() => {
    if (timedOut) return undefined;
    const remaining = Math.max(0, timeoutMs - (smartLaunchElapsedMs() ?? 0));
    const timer = setTimeout(() => setTimedOut(true), remaining);
    return () => clearTimeout(timer);
  }, [timedOut, timeoutMs]);

  return timedOut;
}

/**
 * The waiting screen shown after the window picker and before the chat.
 *
 * Before this existed the chat opened straight away and every question asked
 * during the import came back with "this patient's chart import is still in
 * progress". Waiting here is honest about the same delay instead of spending it
 * on answers that cannot be given.
 *
 * This screen does NOT advance on its own. The chart becoming answerable is
 * only the first of three steps; the SDOH/trajectory and patient-context steps
 * run for several seconds after it. Auto-advancing on chart-readiness alone is
 * what made this window flash the FHIR row and disappear before the other two
 * steps had even started (#38). So the clinician presses Continue, and only
 * once the checklist shows every step resolved — they get to see that the data
 * behind the chat is actually there before they start asking it questions.
 *
 * `slow` reveals a way into the chat anyway. That escape hatch is not optional:
 * a failed import would otherwise leave the clinician watching a spinner with
 * no route forward, which is strictly worse than the behaviour this replaced.
 * It only ever shows alongside the spinner, though -- once `canContinue` is
 * true the real "Continue to the chat" button is the one and only way in, and
 * when `fatal` there is no way in at all (relaunch is the only path), so the
 * hatch is suppressed in both of those cases (#38 recurrence: it used to keep
 * rendering next to "Continue to the chat" once the chart was ready).
 */
function PreparingChartStep({
  displayName,
  patientFhirId,
  phase,
  detail,
  slow,
  chartReady,
  onContinue,
  onSkip,
}: {
  displayName: string;
  patientFhirId: string;
  phase: SmartChartPhase;
  detail: string;
  slow: boolean;
  chartReady: boolean;
  onContinue: () => void;
  onSkip: () => void;
}) {
  // Independent poll of the #35/#36 step journal, which drives both the
  // checklist and the Continue gate below.
  const { status: pipelineStatus } = usePatientPipelineStatus(patientFhirId, {
    activeIntervalMs: 2000,
    // Ticket #38 recurrence: `ready` is Digital-Twin readiness, not step-
    // journal completion -- without this the hook could drop to its 60s idle
    // poll while the checklist above is still mid-build.
    holdActiveUntilStepsFinal: true,
  });
  const steps = pipelineStatus?.steps ?? [];
  const continueGate = pipelineStatus?.continue_gate;

  const [gateFallback, setGateFallback] = useState(false);
  useEffect(() => {
    if (!chartReady) return;
    const timer = setTimeout(() => setGateFallback(true), STEP_GATE_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [chartReady]);

  // Ticket #47 (USER ORDER 2026-08-04): Continue now waits for EVERY step in
  // the journal -- gating or background -- to reach a final status, not just
  // the original 3. That rule lives entirely on the backend
  // (`_derive_continue_gate`): `waiting` means at least one step (including
  // the trajectory summary / clinical synthesis LLM products) has not
  // resolved yet. `areStepsFinal(steps, 3)` is kept ONLY as the fallback for
  // a degraded response with no `continue_gate` field.
  const stepsFinal = continueGate != null ? continueGate !== 'waiting' : areStepsFinal(steps, 3);
  // The steps never resolved and the fallback ran out. This is treated as a
  // SOFT problem and never as a silent pass: the clinician is let through so
  // they are not trapped, but they are told plainly that the build did not
  // finish in the time it normally takes.
  const overdue = chartReady && !stepsFinal && gateFallback;
  // A failed FHIR resource import is fatal for the session. There is no
  // Continue at all here — a chat answering from a chart that is missing
  // records can mislead a clinical decision, so the only way out is a fresh
  // launch.
  const fatal = continueGate === 'hard_block' && (stepsFinal || gateFallback);
  // #53 (USER ORDER 2026-08-06): $everything is now the SOLE fetch call, so
  // its failure means there is no chart at all -- a distinct, more severe
  // case than a partial FHIR-import failure, and the message must say so and
  // point at the System Administrator rather than "launch the patient again".
  const fetchFailed = steps.some(
    (step) => step.step_id === 'fhir_fetch' && step.status === 'error'
  );
  const soft = !fatal && ((continueGate === 'soft_warning' && stepsFinal) || overdue);
  // Continue appears only when the chart can actually answer AND the steps
  // behind it have resolved — that pairing is the entire point of the gate.
  const readyToContinue =
    chartReady && continueGate !== 'hard_block' && (stepsFinal || gateFallback);

  // #56: the overall cap. Note what it deliberately does NOT do — it never
  // overrides a hard block. A launch whose chart could not be fetched has
  // nothing to reason over, and letting the clinician into a chat backed by a
  // missing chart to satisfy a timer would be worse than making them wait
  // (#53). So the cap opens the gate on a SLOW launch, never on a failed one.
  const launchTimedOut = useSmartLaunchTimedOut(SMART_LAUNCH_TIMEOUT_MS);
  const timedOutWaiting =
    launchTimedOut && continueGate !== 'hard_block' && !readyToContinue;

  // One Continue affordance, ever. Folding the timeout into `canContinue`
  // rather than rendering a second button is what keeps #46 fixed: the
  // "Open the chat anyway" hatch below is suppressed whenever this is true,
  // so the timeout can never put two competing ways forward on the screen.
  const canContinue = readyToContinue || timedOutWaiting;

  return (
    <GateShell title={`Loading ${displayName}'s chart`}>
      <div className="mt-6 flex items-center justify-center gap-3 text-sm text-text-tertiary">
        {canContinue ? (
          <CheckCircle2 className="size-4 text-primary" aria-hidden />
        ) : (
          <Loader2 className="size-4 animate-spin text-primary" aria-hidden />
        )}
        <span role="status" aria-live="polite">
          {canContinue ? CHART_PHASE_MESSAGE.ready : CHART_PHASE_MESSAGE[phase]}
        </span>
      </div>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-text-tertiary">
        A large chart can take a couple of minutes. Questions asked before it
        finishes cannot be answered, so every step below has to complete first.
      </p>
      <ImportStepChecklist steps={steps} />
      {fatal ? (
        <GateWarning tone="fatal">
          {fetchFailed ? (
            <>
              This patient&rsquo;s chart could not be retrieved from the
              clinical system, so this session has no data to work from.
              Please contact your System Administrator.
            </>
          ) : (
            <>
              Chart import failed. Some of this patient&rsquo;s records could
              not be loaded, so this session cannot be relied on. Close it
              and launch the patient again from your clinical system. If this
              happens again, contact the MarchAgent team.
            </>
          )}
        </GateWarning>
      ) : null}
      {timedOutWaiting ? (
        <GateWarning tone="soft">
          This chart is still building. You can continue now, but answers may be
          incomplete until it finishes. It keeps building in the background, so
          the chat fills in as it goes.
        </GateWarning>
      ) : soft ? (
        <GateWarning tone="soft">
          {overdue
            ? 'Chart data is taking longer than normal to build. You can continue, but certain capabilities may be unavailable or slower than usual until it finishes.'
            : 'Some chart data did not finish building. You can continue, but certain capabilities may be unavailable until it completes.'}
        </GateWarning>
      ) : null}
      {canContinue ? (
        <div className="mx-auto mt-5 max-w-sm">
          <button type="button" onClick={onContinue} className={CONTINUE_BUTTON_CLASSES}>
            Continue to the chat
          </button>
        </div>
      ) : null}
      {slow && !canContinue && !fatal ? (
        <div className="mx-auto mt-6 max-w-sm">
          <p className="text-sm leading-6 text-text-tertiary">
            {detail || 'This is taking longer than usual.'} You can open the chat
            now, but answers may be incomplete until the chart finishes loading.
          </p>
          <button
            type="button"
            onClick={onSkip}
            className="mt-3 inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-md border border-border bg-surface-hover px-4 text-sm font-medium text-foreground transition-colors hover:bg-surface-sunken focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:w-auto"
          >
            Open the chat anyway
          </button>
        </div>
      ) : null}
    </GateShell>
  );
}

/** Non-blocking notice shown on the live tab while another tab deliberates a switch. */
function ConflictBanner({ label, onDismiss }: { label: string; onDismiss: () => void }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-(--status-attention)/30 bg-status-attention-bg px-4 py-2 text-sm text-status-attention-text"
    >
      <ShieldAlert className="size-4 shrink-0 text-status-attention-text" aria-hidden />
      <span className="min-w-0 flex-1">
        A session for <span className="font-medium">{label}</span> is being opened in another window. Switching there will close this patient.
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 text-status-attention-text transition-colors hover:bg-status-attention-bg/70"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}


function isTerminalSessionError(message: string): boolean {
  return (
    message.includes('belongs to another user') ||
    message === SMART_SESSION_NOT_FOUND_MESSAGE
  );
}

export function SmartEntryGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>({ status: 'checking', phase: 'patient' });
  const [attempt, setAttempt] = useState(0);
  const [passiveConflict, setPassiveConflict] = useState<string | null>(null);
  // The session this tab currently holds as its live patient — the target other
  // tabs address when they announce a conflicting launch.
  const mySessionRef = useRef<string>('');

  const runImportFlow = useCallback((
    sessionId: string,
    presetPatient?: SmartPatientDemographics,
    // The chart-history window this attach should load. Callers that go
    // through a picker pass the clinician's choice; the paths that skip the
    // picker entirely (conflict switch, superseded go-to) pass nothing and get
    // the product default below.
    //
    // No caller may leave this unresolved. The server reads a missing window as
    // "load the entire chart", so every path that forgot to pass one silently
    // loaded years of history nobody asked for. Defaulting in ONE place means
    // no future caller can reintroduce that.
    historyWindow?: SmartHistoryWindow,
    // Resume paths (returning from Admin, adopting a recovered session) set
    // this. The chart is very likely already loaded, and re-fetching a large
    // one from the FHIR server costs MINUTES — measured at over 4 on a real
    // chart — for data that is already on disk. So those paths ask first and
    // import only if the chart is genuinely not ready.
    //
    // A fresh launch never sets it: a new attach must always re-import, because
    // the chart may have changed since the last one.
    reuseLoadedChart = false,
  ) => {
    const appliedWindow: SmartHistoryWindow = historyWindow ?? DEFAULT_HISTORY_WINDOW;
    let cancelled = false;
    setState({ status: 'checking', phase: 'patient' });

    (async () => {
      try {
        let patient = presetPatient;
        if (!patient) {
          const patientResource = await _withTimeout(fetchSmartPatient(sessionId), 'verifying the patient record');
          if (cancelled) return;
          patient = normalizeSmartPatient(patientResource);
        }
        if (cancelled) return;
        // Single active patient per clinician (Requirement 1): before mounting,
        // claim this patient as THE active context across all of this clinician's
        // tabs. If a *different* patient is already live in another tab, stop and
        // let the clinician resolve the conflict rather than opening a second one.
        const decision = getPatientContextCoordinator().claim(claimInputFor(sessionId, patient));
        if (decision.action === 'conflict') {
          // Notify the live owner tab so it can show a passive banner while the
          // clinician here decides. Cleared when they switch or keep.
          getPatientContextCoordinator().announceConflict(
            decision.existing.sessionId,
            patient.displayName || 'a new patient',
          );
          setState({ status: 'conflict', sessionId, patient, existing: decision.existing });
          return;
        }
        mySessionRef.current = sessionId;
        storeSmartWorkspacePatient(patient);
        // Best-effort fallback display value for the disabled read-only
        // readout on a later return to chat — overwritten below if the
        // import/preview response echoes back the window it actually applied.
        storeSmartHistoryWindow(appliedWindow);
        window.dispatchEvent(new CustomEvent('march-smart-patient-ready'));

        // Hold the clinician here until the chart can actually answer a
        // question. The workspace used to mount immediately while the import
        // ran in the background, so the first questions asked came back with
        // "this patient's chart import is still in progress" — a chat that is
        // open but cannot answer anything is worse than an honest wait.
        setState({
          status: 'preparing',
          sessionId,
          patient,
          phase: 'importing',
          detail: '',
          slow: false,
          chartReady: false,
        });

        // Returning from Admin should be instant. Ask before re-fetching: if
        // this session's chart is already loaded under the window it is meant
        // to have, there is nothing to import and nothing to wait for.
        if (reuseLoadedChart) {
          const existing = await fetchSmartChartStatus(sessionId, appliedWindow);
          if (cancelled) return;
          if (existing.ready) {
            if (existing.window) storeSmartHistoryWindow(existing.window);
            setState({ status: 'ready' });
            return;
          }
        }

        // Fire the import, but do not gate on this promise: it is a long
        // request, and a proxy timing it out must not strand the screen. The
        // status poll below reports the real state either way.
        const previewPromise = fetchSmartImportPreview(sessionId, appliedWindow);
        let previewFailed = false;
        let terminalPreviewError: string | null = null;
        void previewPromise
          .then((preview) => {
            const echoed = extractEchoedHistoryWindow(preview);
            if (echoed) storeSmartHistoryWindow(echoed);
          })
          .catch((err: unknown) => {
            // A failed import means the wait below will never clear on its own,
            // so surface the way through immediately instead of making the
            // clinician sit out the full timeout.
            previewFailed = true;
            // TERMINAL import failures used to be swallowed here — the message
            // was discarded, only `slow` was set, and the gate kept polling a
            // chart that could never arrive. A dead session (backend restart)
            // spun "Getting the record…" until the clinician gave up. Capture
            // it and stop the wait NOW.
            const message = err instanceof Error ? err.message : String(err ?? '');
            if (isTerminalSessionError(message)) terminalPreviewError = message;
          });

        await waitForSessionChartReady(sessionId, {
          shouldStop: () => cancelled || terminalPreviewError !== null,
          expectedWindow: appliedWindow,
          onUpdate: (status, elapsedMs) => {
            if (cancelled) return;
            setState((prev) =>
              prev.status === 'preparing'
                ? {
                    ...prev,
                    phase: status.phase,
                    detail: status.detail,
                    slow: previewFailed || elapsedMs >= CHART_READY_SLOW_AFTER_MS,
                  }
                : prev,
            );
          },
        });
        if (cancelled) return;
        if (terminalPreviewError) {
          clearPatientConnectionState();
          setState({
            status: 'blocked',
            message:
              'This patient connection has expired (the server restarted). Open the patient record again from your clinical system to continue.',
          });
          return;
        }
        // No step journal in this deployment (an empty VITE_MARCH_API_URL means
        // the API is same-origin, and `isPatientPipelineApiAvailable` is false
        // there, so the checklist never polls). With no steps to review there
        // is nothing for a Continue button to gate on, and holding the screen
        // would strand the clinician for the whole fallback timeout — so keep
        // the pre-#38 behaviour exactly in that configuration.
        if (!isPatientPipelineApiAvailable()) {
          setState({ status: 'ready' });
          return;
        }
        // The chart can answer questions now — but do NOT mount the chat here.
        // The SDOH/trajectory and patient-context steps only START several
        // seconds after this point (measured on dev: chart ready at +1.2s,
        // those two steps ran at +9.4s to +10.6s). Auto-advancing here is
        // exactly what made the waiting window show the FHIR row and then
        // vanish before the remaining steps existed (#38). Hold the gate and
        // let the clinician press Continue once the checklist is complete, so
        // they never enter the chat while the data behind it is still being
        // built.
        setState((prev) => (prev.status === 'preparing' ? { ...prev, chartReady: true } : prev));
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error
          ? err.message
          : 'The patient record connection is not responding. Retry, or open the patient record again from your clinical system.';
        // TERMINAL failures: retrying the same stored session can never
        // succeed, so clear it and ask for a fresh EHR launch instead of
        // dead-ending on "Try again".
        //  - ownership 403: the stored session was bound to a different
        //    sign-in (shared dev launcher / shared workstation);
        //  - session not found: the backend restarted and the in-memory
        //    session is gone.
        if (isTerminalSessionError(message)) {
          clearPatientConnectionState();
          setState({
            status: 'blocked',
            message:
              'This patient connection belonged to a different sign-in or has expired. Open the patient record again from your clinical system to continue.',
          });
          return;
        }
        // Transient failures keep the connection: clearing would turn a
        // retryable hiccup into a forced re-launch.
        setState({ status: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Shared-workstation isolation: if the authenticated clinician changed since
    // the last visit on this browser, clear the prior clinician's SMART state
    // BEFORE we capture/resolve — so a fresh launch is never wiped, but a stale
    // cross-tab recovery cache can never surface to the new clinician.
    reconcileClinicianScope();
    capturePendingSmartLaunchFromLocation();
    applyPendingSmartLaunchSession();

    const pendingError = validatePendingSmartLaunch(getPendingSmartLaunch());
    if (pendingError) {
      setState({ status: 'error', message: pendingError });
      return undefined;
    }

    if (requiresMarchAgentBearer() && !hasMarchAgentBearer()) {
      setState({
        status: 'blocked',
        message: 'Sign in to MarchAgent to continue with this patient record.',
      });
      return undefined;
    }

    const resolution = resolveSmartSessionFromLocationDetailed();
    if (!resolution) {
      // The dev/no-EHR bypass applies ONLY when there is no SMART context at
      // all. It must never swallow a real launch: with dev Keycloak login,
      // hasDevAccessToken() is true right after sign-in, and bypassing here
      // used to skip the patient import entirely — stranding the clinician on
      // an empty workspace even though the launch session was captured.
      if (shouldBypassSmartLaunch()) {
        setState({ status: 'ready' });
        return undefined;
      }
      setState({ status: 'blocked', message: 'Open a patient record from your clinical system to continue.' });
      return undefined;
    }

    if (resolution.source === 'tab') {
      // This tab's OWN earlier session — e.g. returning from the Admin panel.
      // USER ORDER (2026-07-31): go straight back into the chat, exactly like
      // navigating to the Digital Twin page and back does. The confirm screen
      // and the readiness wait were re-shown here, and on a large chart the
      // wait meant a minute-plus pause to return to a chat that was already
      // loaded. There is nothing to confirm (the tab never left its patient)
      // and nothing to load (the chart is the one this tab was just using).
      // Cross-tab adoption ('recovered' below) keeps its explicit confirm —
      // that one IS a wrong-patient hazard; a same-tab return is not.
      const tabSessionId = resolution.sessionId;
      let cancelled = false;
      setState({ status: 'checking', phase: 'patient' });
      (async () => {
        try {
          // One lightweight liveness probe so a dead session (backend restart,
          // ownership change) still lands on the blocked screen instead of an
          // empty workspace — but NO confirm step and NO import/readiness wait.
          const patientResource = await fetchSmartPatient(tabSessionId);
          if (cancelled) return;
          const patient = normalizeSmartPatient(patientResource);
          mySessionRef.current = tabSessionId;
          storeSmartWorkspacePatient(patient);
          window.dispatchEvent(new CustomEvent('march-smart-patient-ready'));
          setState({ status: 'ready' });
        } catch (err) {
          if (cancelled) return;
          const message = err instanceof Error
            ? err.message
            : 'The previous patient record connection is no longer available. Open the patient record again to continue.';
          if (isTerminalSessionError(message)) {
            clearPatientConnectionState();
            setState({
              status: 'blocked',
              message:
                'This patient connection belonged to a different sign-in or has expired. Open the patient record again from your clinical system to continue.',
            });
            return;
          }
          setState({ status: 'error', message });
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (resolution.source === 'recovered') {
      // The session was found only in the cross-tab recovery cache — the app
      // was closed and re-opened. This is the one stored-state path that keeps
      // its explicit confirmation: the recovered session is whichever patient
      // was launched most recently in ANY tab, so silently re-attaching is a
      // wrong-patient hazard. It is also, by product rule, the only route to
      // changing the history window — hence the interactive picker.
      let cancelled = false;
      setState({ status: 'checking', phase: 'patient' });
      (async () => {
        try {
          const patientResource = await fetchSmartPatient(resolution.sessionId);
          if (cancelled) return;
          const patient = normalizeSmartPatient(patientResource);
          setState({ status: 'confirm-recovered', sessionId: resolution.sessionId, patient });
        } catch (err) {
          if (cancelled) return;
          const message = err instanceof Error
            ? err.message
            : 'The previous patient record connection is no longer available. Open the patient record again to continue.';
          if (isTerminalSessionError(message)) {
            // Bound to a different sign-in or gone after a restart: the stored
            // session can never work again — clear it, ask for a fresh launch.
            clearPatientConnectionState();
            setState({
              status: 'blocked',
              message:
                'This patient connection belonged to a different sign-in or has expired. Open the patient record again from your clinical system to continue.',
            });
            return;
          }
          // Transient (network / cold backend): keep the stored session so
          // Try again can succeed.
          setState({ status: 'error', message });
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // resolution.source === 'launch': a genuine first attach for this
    // session (real EHR launch params on the URL). Fetch the patient
    // identity so the window-selection screen can show WHO before asking
    // HOW MUCH history to load — the import itself only starts once the
    // clinician confirms via runImportFlow below.
    let cancelled = false;
    setState({ status: 'checking', phase: 'patient' });
    (async () => {
      try {
        const patientResource = await fetchSmartPatient(resolution.sessionId);
        if (cancelled) return;
        const patient = normalizeSmartPatient(patientResource);
        setState({ status: 'select-window', sessionId: resolution.sessionId, patient });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error
          ? err.message
          : 'The patient record connection is not responding. Retry, or open the patient record again from your clinical system.';
        if (isTerminalSessionError(message)) {
          clearPatientConnectionState();
          setState({
            status: 'blocked',
            message:
              'This patient connection belonged to a different sign-in or has expired. Open the patient record again from your clinical system to continue.',
          });
          return;
        }
        setState({ status: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runImportFlow, attempt]);

  // Requirement 1: while this tab is live, another tab of the same clinician can
  // take over the active patient context (a confirmed switch). When that happens
  // this tab is superseded and must stop showing its now-stale patient — the
  // gate reclaims the screen with a lock, tearing the workspace down immediately.
  useEffect(() => {
    return getPatientContextCoordinator().onSuperseded((active) => {
      setPassiveConflict(null);
      setState({ status: 'superseded', active });
    });
  }, []);

  // Passive banner: another tab is deliberating a switch away from THIS patient.
  useEffect(() => {
    const coord = getPatientContextCoordinator();
    const offPending = coord.onConflictPending((signal) => {
      if (signal.targetSessionId && signal.targetSessionId === mySessionRef.current) {
        setPassiveConflict(signal.newPatientLabel || 'another patient');
      }
    });
    const offCleared = coord.onConflictCleared((signal) => {
      if (signal.targetSessionId === mySessionRef.current) setPassiveConflict(null);
    });
    return () => {
      offPending();
      offCleared();
    };
  }, []);

  if (state.status === 'ready') {
    // Task #28: every way into the chat area -- a fresh SMART launch, returning
    // from Admin, and cross-tab recovery -- converges HERE before the workspace
    // is handed the screen, so one mount point covers all of them. It is
    // deliberately not placed in the "Resume patient session?" dialog below:
    // that dialog is skipped entirely on a fresh launch, which is exactly when
    // the twin is least likely to have finished building.
    const workspacePatient = getStoredSmartWorkspacePatient();
    const readinessNotice = <TwinReadinessNotice patientId={workspacePatient?.mrn || ''} />;
    if (!passiveConflict) {
      return (
        <>
          {readinessNotice}
          {children}
        </>
      );
    }
    return (
      <>
        <ConflictBanner label={passiveConflict} onDismiss={() => setPassiveConflict(null)} />
        {readinessNotice}
        {children}
      </>
    );
  }

  if (state.status === 'checking') {
    // Brief identity resolution only — the clinical-record import no longer
    // blocks here; it runs in the background once the workspace has mounted.
    return (
      <GateShell
        title="Opening patient record"
        message="Verifying the SMART session…"
      />
    );
  }

  if (state.status === 'preparing') {
    const displayName = state.patient.displayName || 'this patient';
    return (
      <PreparingChartStep
        displayName={displayName}
        patientFhirId={state.patient.fhirId || ''}
        phase={state.phase}
        detail={state.detail}
        slow={state.slow}
        chartReady={state.chartReady}
        onContinue={() => setState({ status: 'ready' })}
        onSkip={() => setState({ status: 'ready' })}
      />
    );
  }

  if (state.status === 'confirm-recovered') {
    const displayName = state.patient.displayName || 'Unknown patient';
    const identifier = state.patient.mrn || state.patient.fhirId || state.patient.accountNumber || '';
    return (
      <ConfirmRecoveredStep
        displayName={displayName}
        identifier={identifier}
        onContinue={(window) => {
          adoptRecoveredPatientConnection(state.sessionId);
          // The chart for this session is usually already loaded server-side,
          // so ask readiness first and reuse it rather than spending minutes
          // re-fetching what is on disk (see runImportFlow's reuse path).
          runImportFlow(state.sessionId, state.patient, window, true);
        }}
      />
    );
  }

  if (state.status === 'select-window') {
    // First attach of this session (a genuine EHR launch) — the
    // chart-history window picker is interactive. Always defaults to
    // "2 years", never read from storage.
    const displayName = state.patient.displayName || 'Unknown patient';
    const identifier = state.patient.mrn || state.patient.fhirId || state.patient.accountNumber || '';
    return (
      <SelectWindowStep
        displayName={displayName}
        identifier={identifier}
        onContinue={(w) => {
          // Spend the launch handoff here, at the moment of commitment, so
          // that a later in-app navigation (Admin and back) correctly resolves
          // as a resume with the picker locked — while a page reload BEFORE
          // choosing still lands back on this picker rather than skipping past
          // the only chance to set the window.
          consumeSmartLaunchHandoff();
          runImportFlow(state.sessionId, state.patient, w);
        }}
      />
    );
  }

  if (state.status === 'conflict') {
    const newName = state.patient.displayName || 'the new patient';
    const openName = state.existing.patientLabel || 'another patient';
    return (
      <GateShell
        title="Another patient is already open"
        message="Only one patient can be open at a time on this browser. You already have a patient open in another window."
      >
        <div className="mx-auto mt-5 grid max-w-sm gap-2 text-left">
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-background/70 p-4">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-sunken text-text-tertiary" aria-hidden>
              <UserCheck className="size-5" />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-text-tertiary">Already open</div>
              <div className="truncate text-sm font-medium text-foreground">{openName}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-primary" aria-hidden>
              <UserCheck className="size-5" />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-primary">Just launched</div>
              <div className="truncate text-sm font-medium text-foreground">{newName}</div>
            </div>
          </div>
        </div>
        <p className="mx-auto mt-3 flex max-w-sm items-start gap-1.5 text-left text-xs text-text-tertiary">
          <ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-status-attention-text" aria-hidden />
          Switching closes {openName} everywhere on this browser to prevent mixing records.
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => {
              getPatientContextCoordinator().supersede(claimInputFor(state.sessionId, state.patient));
              getPatientContextCoordinator().clearConflict(state.existing.sessionId);
              // Evict the superseded patient's server session so it cannot be reused.
              void closeSmartSession(state.existing.sessionId);
              runImportFlow(state.sessionId, state.patient);
            }}
            className="inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:w-auto"
          >
            Switch to {newName}
          </button>
          <button
            type="button"
            onClick={() => {
              // Abandon this just-launched session and keep the open patient.
              getPatientContextCoordinator().clearConflict(state.existing.sessionId);
              void closeSmartSession(state.sessionId);
              setState({
                status: 'blocked',
                message: `You're still working with ${openName} in another window. Return to that window to continue, or close it before opening a different patient.`,
              });
            }}
            className="inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-md border border-border bg-background px-4 text-sm font-medium transition-colors hover:bg-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:w-auto"
          >
            Keep {openName}
          </button>
        </div>
      </GateShell>
    );
  }

  if (state.status === 'superseded') {
    const activeName = state.active.patientLabel || 'another patient';
    return (
      <GateShell
        title="This patient session was closed"
        message={`You switched to ${activeName} in another window. This patient is no longer open here to prevent mixing records.`}
      >
        <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => {
              adoptRecoveredPatientConnection(state.active.sessionId);
              // Adopting a session another tab already opened — its chart is
              // loaded, so reuse it instead of re-importing (see runImportFlow).
              runImportFlow(state.active.sessionId, undefined, getStoredSmartHistoryWindow() ?? undefined, true);
            }}
            className="inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:w-auto"
          >
            Go to {activeName}
          </button>
          <button
            type="button"
            onClick={() => {
              setState({
                status: 'blocked',
                message: `${activeName} is open in another window. Open a patient record from your clinical system to work here.`,
              });
            }}
            className="inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-md border border-border bg-background px-4 text-sm font-medium transition-colors hover:bg-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:w-auto"
          >
            Not now
          </button>
        </div>
      </GateShell>
    );
  }

  if (state.status === 'error') {
    return (
      <GateShell title="Patient connection unavailable" message={state.message}>
        <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:w-auto"
          >
            Try again
          </button>
        </div>
        <p className="mt-3 text-xs text-text-tertiary">
          If this keeps failing, open the patient record again from your clinical system.
        </p>
      </GateShell>
    );
  }

  return <GateShell title="Patient record required" message={state.message ?? 'Open a patient record from your clinical system to continue.'} />;
}
